import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Building2, Check, RefreshCw, User } from 'lucide-react';
import api from '../../services/apiService';
import { UserContext } from '../../contexts/UserContext';
import { buildUserData, dashboardPathFor } from '../../auth/sessionUser';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

// Plan v2 E3: a session that began with the person's private account switches between it and the
// company accounts linked to it. Renders nothing for any other session (a company login cannot
// reach the private account — D-A4). Entering a company that enforces two-factor asks for the code
// sent to the company address, like a login there.
const AccountSwitcher = ({ onSwitched }) => {
  const { login } = useContext(UserContext);
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [awaiting, setAwaiting] = useState([]);   // D-B8: switched back on by a company, not confirmed yet
  const [canSwitch, setCanSwitch] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null);   // {access} waiting for a 2FA code
  const [code, setCode] = useState('');

  useEffect(() => {
    let alive = true;
    api.get('/accounts/mine/')
      .then(({ data }) => {
        if (!alive) return;
        setCanSwitch(Boolean(data?.can_switch));
        setAccounts(data?.accounts || []);
        setAwaiting(data?.awaiting_confirmation || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const finish = async (access) => {
    const { data } = await axios.get(`${API_BASE_URL}/user-info/`, {
      headers: { Authorization: `Bearer ${access}` },
    });
    const userData = buildUserData(data, access);
    login(userData);
    if (onSwitched) onSwitched();
    navigate(dashboardPathFor(userData.role), { replace: true });
  };

  const switchTo = async (account) => {
    if (account.current || busy) return;
    setBusy(account.id);
    setError(null);
    try {
      const { data } = await api.post('/token/switch/', { account_id: account.id }, {
        withCredentials: true,   // the new refresh token arrives as the HttpOnly cookie
      });
      if (data.two_factor_pending) {
        await axios.post(`${API_BASE_URL}/send-verification-email/`, {}, {
          headers: { Authorization: `Bearer ${data.access}` },
        });
        setPending({ access: data.access, label: account.label });
        return;
      }
      await finish(data.access);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not switch accounts.');
    } finally {
      setBusy(null);
    }
  };

  const answer = async (account, accept) => {
    setError(null);
    try {
      await api.post(`/accounts/${account.id}/link/`, { accept });
      setAwaiting((prev) => prev.filter((a) => a.id !== account.id));
      if (accept) setAccounts((prev) => [...prev, { ...account, current: false }]);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your answer.');
    }
  };

  const verify = async () => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}/verify-code/`, { code }, {
        headers: { Authorization: `Bearer ${pending.access}` },
      });
      if (!data.success) {
        setError('Invalid verification code');
        return;
      }
      const access = pending.access;
      setPending(null);
      setCode('');
      await finish(access);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
    }
  };

  if (!canSwitch) return null;

  return (
    <div className="p-1 border-b border-slate-100" data-account-switcher>
      <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold text-slate-400 uppercase tracking-wide">Switch account</p>
      {accounts.map((a) => (
        <button
          key={a.id}
          onClick={() => switchTo(a)}
          disabled={Boolean(busy)}
          className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
            a.current ? 'text-brand-700 bg-brand-50' : 'text-slate-700 hover:bg-slate-50'
          }`}
        >
          {a.kind === 'private'
            ? <User size={14} className="text-slate-400" />
            : <Building2 size={14} className="text-slate-400" />}
          <span className="truncate">{a.label}</span>
          {busy === a.id && <RefreshCw size={12} className="ml-auto animate-spin text-slate-400" />}
          {a.current && <Check size={14} className="ml-auto text-brand-600" />}
        </button>
      ))}
      {awaiting.map((a) => (
        <div key={`awaiting-${a.id}`} className="mx-1 my-1 px-2 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-[11px] text-amber-800">
            <span className="font-bold">{a.label}</span> switched your account back on. Add it to your accounts?
          </p>
          <div className="flex gap-2 mt-1">
            <button onClick={() => answer(a, true)} className="px-2 py-0.5 text-[11px] font-bold text-white bg-brand-600 rounded">
              Confirm
            </button>
            <button onClick={() => answer(a, false)} className="px-2 py-0.5 text-[11px] font-bold text-slate-600 bg-white border border-slate-300 rounded">
              Decline
            </button>
          </div>
        </div>
      ))}
      {error && <p className="px-3 py-1 text-[11px] text-red-600">{error}</p>}
      {pending && (
        <div className="px-3 py-2 space-y-2">
          <p className="text-[11px] text-slate-600">Enter the code we sent for {pending.label}.</p>
          <div className="flex gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              maxLength={6}
              className="flex-1 min-w-0 px-2 py-1 text-xs border border-slate-300 rounded-md"
              placeholder="123456"
            />
            <button onClick={verify} className="px-2 py-1 text-xs font-bold text-white bg-brand-600 rounded-md">
              Verify
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
